import { useCallback, useEffect, useState } from 'react';

import { notifications } from '@mantine/notifications';

import { vnextApiClient } from '@/services/vnext-api';
import {
  ScopeComponentCapability,
  ScopeComponentCapabilityCreateRequest,
  ScopeComponentCapabilityUpdateRequest,
} from '@/types';

interface UseScopeComponentCapabilitiesOptions {
  componentId: string | null;
  autoFetch?: boolean;
}

export function useScopeComponentCapabilities({
  componentId,
  autoFetch = true,
}: UseScopeComponentCapabilitiesOptions) {
  const [capabilities, setCapabilities] = useState<ScopeComponentCapability[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchCapabilities = useCallback(async () => {
    if (!componentId) {
      setCapabilities([]);
      return [];
    }
    try {
      setIsLoading(true);
      setError(null);
      const data = await vnextApiClient.listScopeComponentCapabilities(componentId);
      setCapabilities(data);
      return data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch capabilities');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [componentId]);

  const createCapability = useCallback(
    async (data: ScopeComponentCapabilityCreateRequest): Promise<ScopeComponentCapability> => {
      if (!componentId) {
        throw new Error('No component selected');
      }
      try {
        const created = await vnextApiClient.createScopeComponentCapability(componentId, data);
        notifications.show({
          title: 'Capability added',
          message: 'New capability saved.',
          color: 'green',
        });
        await fetchCapabilities();
        return created;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create capability');
        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });
        throw error;
      }
    },
    [componentId, fetchCapabilities]
  );

  const updateCapability = useCallback(
    async (
      capabilityId: string,
      data: ScopeComponentCapabilityUpdateRequest
    ): Promise<ScopeComponentCapability> => {
      if (!componentId) {
        throw new Error('No component selected');
      }
      try {
        const updated = await vnextApiClient.updateScopeComponentCapability(
          componentId,
          capabilityId,
          data
        );
        notifications.show({
          title: 'Capability updated',
          message: 'Capability changes saved.',
          color: 'green',
        });
        await fetchCapabilities();
        return updated;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update capability');
        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });
        throw error;
      }
    },
    [componentId, fetchCapabilities]
  );

  const deleteCapability = useCallback(
    async (capabilityId: string): Promise<void> => {
      if (!componentId) {
        throw new Error('No component selected');
      }
      try {
        await vnextApiClient.deleteScopeComponentCapability(componentId, capabilityId);
        notifications.show({
          title: 'Capability removed',
          message: 'Capability deleted.',
          color: 'green',
        });
        await fetchCapabilities();
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to delete capability');
        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });
        throw error;
      }
    },
    [componentId, fetchCapabilities]
  );

  useEffect(() => {
    if (autoFetch && componentId) {
      fetchCapabilities();
    }
  }, [autoFetch, componentId, fetchCapabilities]);

  return {
    capabilities,
    isLoading,
    error,
    fetchCapabilities,
    createCapability,
    updateCapability,
    deleteCapability,
  };
}
